import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent
} from 'react';
import {
  Archive,
  BadgeCheck,
  CalendarClock,
  ChevronRight,
  CircleAlert,
  Download,
  Eye,
  FileCheck2,
  FilePlus2,
  FileText,
  Filter,
  FolderCog,
  History,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X
} from 'lucide-react';
import { readToken, tenantTokenKey } from '../../app/session';
import { CroatianDateInput } from '../../components/CroatianDateInput';
import { MobileVirtualList, useMobileViewport } from '../../components/MobileVirtualList';
import {
  SearchableSelect,
  type SearchableSelectOption
} from '../../components/SearchableSelect';
import { SwipeReveal } from '../../components/SwipeReveal';
import { useTranslation } from '../../i18n/i18n';
import { ApiError, apiDownload, apiRequest } from '../../lib/api';
import { useTenantApp } from '../app/TenantAppContext';

interface DocumentItem {
  id: string;
  ownerUserId: string | null;
  ownerName: string | null;
  category: string;
  categoryId: string | null;
  categoryName: string;
  categoryColor: string;
  title: string;
  description: string | null;
  visibility: 'OWNER' | 'MENTOR' | 'TEAM_MANAGERS' | 'COMPANY_ADMINS';
  validFrom: string | null;
  validUntil: string | null;
  requiresAcknowledgement: boolean;
  acknowledgedAt: string | null;
  acknowledgementCount: number;
  createdAt: string;
  updatedAt: string;
  version: {
    id: string;
    number: number;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  };
}

interface UserOption {
  id: string;
  employeeCode: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
}

interface Category {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string;
  sortOrder: number;
  documentCount: number;
}

interface Acknowledgement {
  userId: string;
  userName: string;
  versionNumber: number;
  acknowledgedAt: string;
}

interface DocumentVersion {
  id: string;
  number: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedByName: string;
}

const emptyForm = {
  ownerUserId: '', categoryId: '', category: 'Općenito', title: '', description: '',
  visibility: 'OWNER', validFrom: '', validUntil: '', requiresAcknowledgement: false,
  originalFilename: '', fileDataUrl: ''
};
const emptyCategory = {
  code: '', name: '', description: '', color: '#1C3667', sortOrder: 100
};
const statusLabels: Record<string, string> = {
  ALL: 'Svi dokumenti',
  VALID: 'Važeći',
  EXPIRING: 'Istječu u 30 dana',
  EXPIRED: 'Istekli',
  NO_EXPIRY: 'Bez roka',
  ACK_PENDING: 'Čeka moju potvrdu'
};
const visibilityLabels: Record<string, string> = {
  OWNER: 'Vlasnik dokumenta',
  MENTOR: 'Vlasnik i njegov mentor',
  TEAM_MANAGERS: 'Vlasnik i voditelj njegova tima',
  COMPANY_ADMINS: 'Vlasnik i administratori firme'
};

function documentCountText(count: number): string {
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} dokumenata`;
  if (count % 10 === 1) return `${count} dokument`;
  if (count % 10 >= 2 && count % 10 <= 4) return `${count} dokumenta`;
  return `${count} dokumenata`;
}

async function readFileDataUrl(file: File): Promise<string> {
  if (file.size > 8 * 1024 * 1024) throw new Error('Datoteka mora biti manja od 8 MB.');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Datoteku nije moguće pročitati.'));
    reader.readAsDataURL(file);
  });
}

export function DocumentsPage() {
  const { context } = useTenantApp();
  const { locale, t } = useTranslation();
  const canManage = context.user.permissions.includes('documents.manage');
  const canCategories = context.user.permissions.includes('document_categories.manage');
  const isMobile = useMobileViewport();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<DocumentItem | null>(null);
  const [detail, setDetail] = useState<DocumentItem | null>(null);
  const [acknowledgements, setAcknowledgements] = useState<Acknowledgement[]>([]);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<Category | null>(null);
  const [replacementCategoryId, setReplacementCategoryId] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [preview, setPreview] = useState<{ title: string; url: string; mimeType: string } | null>(null);
  const [versionModal, setVersionModal] = useState(false);
  const [versionFile, setVersionFile] = useState({ originalFilename: '', fileDataUrl: '' });
  const [archiveTarget, setArchiveTarget] = useState<DocumentItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [notice, setNotice] = useState('');

  const token = () => readToken(tenantTokenKey);

  const userOptions = useMemo<SearchableSelectOption[]>(() => users.map((user) => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName}`,
    description: `${user.employeeCode} · ${user.email}${user.jobTitle ? ` · ${user.jobTitle}` : ''}`,
    searchText: `${user.firstName} ${user.lastName} ${user.employeeCode} ${user.email} ${user.jobTitle ?? ''}`
  })), [users]);

  async function load() {
    setError('');
    try {
      const query = new URLSearchParams();
      if (search) query.set('search', search);
      if (ownerFilter) query.set('ownerUserId', ownerFilter);
      if (categoryFilter) query.set('categoryId', categoryFilter);
      if (statusFilter !== 'ALL') query.set('status', statusFilter);
      const [docs, cats, people] = await Promise.all([
        apiRequest<{ items: DocumentItem[] }>(`/api/v1/documents?${query}`, { accessToken: token() }),
        apiRequest<{ items: Category[] }>('/api/v1/documents/categories', { accessToken: token() }),
        canManage
          ? apiRequest<{ items: UserOption[] }>('/api/v1/documents/people', { accessToken: token() })
          : Promise.resolve({ items: [] as UserOption[] })
      ]);
      setDocuments(docs.items);
      setCategories(cats.items);
      setUsers(people.items);
      const requestedId = new URLSearchParams(window.location.search).get('document');
      if (requestedId) {
        const requested = docs.items.find((item) => item.id === requestedId);
        if (requested) void openDetail(requested);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Dokumenti nisu dostupni.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(timer);
  }, [search, ownerFilter, categoryFilter, statusFilter]);

  async function chooseFile(event: ChangeEvent<HTMLInputElement>, version = false) {
    const file = event.target.files?.[0];
    if (!file) return;
    setModalError('');
    try {
      const fileDataUrl = await readFileDataUrl(file);
      if (version) setVersionFile({ originalFilename: file.name, fileDataUrl });
      else setForm({ ...form, originalFilename: file.name, fileDataUrl });
    } catch (caught) {
      setModalError(caught instanceof Error ? caught.message : 'Datoteka nije ispravna.');
    }
  }

  function categoryChanged(id: string) {
    const category = categories.find((item) => item.id === id);
    setForm({ ...form, categoryId: id, category: category?.name ?? '' });
  }

  function openNew() {
    if (!categories.length) {
      setError('Prvo izradite barem jednu kategoriju dokumenta.');
      return;
    }
    const category = categories[0]!;
    setEditing(null);
    setForm({ ...emptyForm, categoryId: category.id, category: category.name });
    setModalError('');
    setModal(true);
  }

  function openEdit(document: DocumentItem) {
    setEditing(document);
    setForm({
      ownerUserId: document.ownerUserId ?? '', categoryId: document.categoryId ?? '',
      category: document.categoryName, title: document.title,
      description: document.description ?? '', visibility: document.visibility,
      validFrom: document.validFrom?.slice(0, 10) ?? '',
      validUntil: document.validUntil?.slice(0, 10) ?? '',
      requiresAcknowledgement: document.requiresAcknowledgement,
      originalFilename: document.version.filename, fileDataUrl: ''
    });
    setModalError('');
    setModal(true);
  }

  async function openDetail(document: DocumentItem) {
    setDetail(document);
    setAcknowledgements([]);
    setVersions([]);
    setModalError('');
    const requests: Array<Promise<void>> = [
      apiRequest<{ items: DocumentVersion[] }>(
        `/api/v1/documents/${document.id}/versions`, { accessToken: token() }
      ).then((response) => setVersions(response.items)).catch(() => undefined)
    ];
    if (canManage) requests.push(apiRequest<{ items: Acknowledgement[] }>(
      `/api/v1/documents/${document.id}/acknowledgements`, { accessToken: token() }
    ).then((response) => setAcknowledgements(response.items)).catch(() => undefined));
    void Promise.all(requests);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setModalError('');
    try {
      const body = editing
        ? (({ originalFilename: _filename, fileDataUrl: _data, ...metadata }) => metadata)(form)
        : form;
      await apiRequest(editing ? `/api/v1/documents/${editing.id}` : '/api/v1/documents', {
        method: editing ? 'PATCH' : 'POST', accessToken: token(), body: JSON.stringify(body)
      });
      setModal(false);
      setForm(emptyForm);
      setNotice(editing ? 'Podaci dokumenta su spremljeni.' : 'Dokument je sigurno spremljen.');
      await load();
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Dokument nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function download(document: DocumentItem) {
    try {
      const result = await apiDownload(`/api/v1/documents/${document.id}/download`, token());
      const url = URL.createObjectURL(result.blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Preuzimanje nije uspjelo.');
    }
  }

  async function downloadVersion(documentId: string, version: DocumentVersion) {
    try {
      const result = await apiDownload(
        `/api/v1/documents/${documentId}/versions/${version.id}/download`, token()
      );
      const url = URL.createObjectURL(result.blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Verziju nije moguće preuzeti.');
    }
  }

  async function showPreview(document: DocumentItem) {
    try {
      const result = await apiDownload(`/api/v1/documents/${document.id}/preview`, token());
      const url = URL.createObjectURL(result.blob);
      setPreview({ title: document.title, url, mimeType: document.version.mimeType });
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Pregled nije dostupan.');
    }
  }

  function closePreview() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function acknowledge(document: DocumentItem) {
    setBusy(true);
    try {
      await apiRequest(`/api/v1/documents/${document.id}/acknowledge`, {
        method: 'POST', accessToken: token()
      });
      setNotice('Čitanje dokumenta je potvrđeno.');
      setDetail(null);
      await load();
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Potvrdu nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function uploadVersion(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    setModalError('');
    try {
      await apiRequest(`/api/v1/documents/${detail.id}/versions`, {
        method: 'POST', accessToken: token(), body: JSON.stringify(versionFile)
      });
      setVersionModal(false);
      setVersionFile({ originalFilename: '', fileDataUrl: '' });
      setDetail(null);
      setNotice('Nova verzija je spremljena; potvrde čitanja računaju se iznova.');
      await load();
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Novu verziju nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function archiveDocument() {
    if (!archiveTarget) return;
    setBusy(true);
    setModalError('');
    try {
      await apiRequest(`/api/v1/documents/${archiveTarget.id}`, {
        method: 'DELETE', accessToken: token()
      });
      setArchiveTarget(null);
      setDetail(null);
      setNotice('Dokument je arhiviran i više nije dostupan korisnicima.');
      await load();
    } catch (caught) {
      setModalError(caught instanceof ApiError ? caught.message : 'Dokument nije moguće arhivirati.');
    } finally {
      setBusy(false);
    }
  }

  function openCategory(category?: Category) {
    setEditingCategory(category ?? null);
    setCategoryForm(category ? {
      code: category.code, name: category.name,
      description: category.description ?? '', color: category.color,
      sortOrder: category.sortOrder
    } : emptyCategory);
    setCategoryError('');
    setCategoryModal(true);
  }

  async function saveCategory(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setCategoryError('');
    try {
      await apiRequest(editingCategory
        ? `/api/v1/documents/categories/${editingCategory.id}`
        : '/api/v1/documents/categories', {
        method: editingCategory ? 'PATCH' : 'POST', accessToken: token(),
        body: JSON.stringify(categoryForm)
      });
      setCategoryModal(false);
      await load();
    } catch (caught) {
      setCategoryError(caught instanceof ApiError ? caught.message : 'Kategoriju nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  function askToDeleteCategory() {
    if (!editingCategory) return;
    setDeleteCategoryTarget(editingCategory);
    setReplacementCategoryId('');
    setCategoryError('');
    setCategoryModal(false);
  }

  async function deleteCategory(event: FormEvent) {
    event.preventDefault();
    if (!deleteCategoryTarget) return;
    setBusy(true);
    setCategoryError('');
    try {
      await apiRequest(`/api/v1/documents/categories/${deleteCategoryTarget.id}`, {
        method: 'DELETE', accessToken: token(), body: JSON.stringify({ replacementCategoryId })
      });
      if (categoryFilter === deleteCategoryTarget.id) setCategoryFilter('');
      setDeleteCategoryTarget(null);
      setReplacementCategoryId('');
      await load();
    } catch (caught) {
      setCategoryError(caught instanceof ApiError ? caught.message : 'Kategoriju nije moguće obrisati.');
    } finally {
      setBusy(false);
    }
  }

  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: 'short', year: 'numeric'
  }).format(new Date(`${value.slice(0, 10)}T12:00:00`));
  const formatDateTime = (value: string) => new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(new Date(value));
  const formatSize = (bytes: number) => bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  const expiryState = (document: DocumentItem) => {
    if (!document.validUntil) return 'NO_EXPIRY';
    const days = Math.ceil((new Date(`${document.validUntil.slice(0, 10)}T12:00:00`).getTime() - Date.now()) / 86400000);
    if (days < 0) return 'EXPIRED';
    if (days <= 30) return 'EXPIRING';
    return 'VALID';
  };
  const expiryLabel = (state: string) => state === 'EXPIRED'
    ? 'Istekao'
    : state === 'EXPIRING'
      ? 'Uskoro istječe'
      : state === 'NO_EXPIRY'
        ? 'Bez roka'
        : 'Važeći';
  const expired = documents.filter((document) => expiryState(document) === 'EXPIRED').length;
  const expiring = documents.filter((document) => expiryState(document) === 'EXPIRING').length;
  const waitingAck = documents.filter((document) => document.requiresAcknowledgement && !document.acknowledgedAt).length;

  return (
    <div className="module-page documents-page">
      <header className="module-page__hero">
        <div><span className="eyebrow">SIGURNO · PRIVATNO · VERZIONIRANO</span><h1>{t('documents.title')}</h1><p>Dokumenti zaposlenika, rokovi, verzije i potvrde čitanja bez traženja po mapama.</p></div>
        <div className="header-actions">{canCategories && <button className="button button--ghost" onClick={() => openCategory()}><FolderCog /> Kategorije</button>}{canManage && <button className="button button--primary" data-mobile-fab onClick={openNew} aria-label="Novi dokument"><Plus /> {t('documents.new')}</button>}</div>
      </header>

      {notice && <button className="leave-notice" onClick={() => setNotice('')}><BadgeCheck /> {notice}<X /></button>}
      {error && !modal && <div className="form-error">{error}</div>}

      <section className="document-summary">
        <article><FileText /><div><strong>{documents.length}</strong><small>U trenutačnom prikazu</small></div></article>
        <article className={expired ? 'has-alert' : ''}><CircleAlert /><div><strong>{expired}</strong><small>Isteklih dokumenata</small></div></article>
        <article className={expiring ? 'has-warning' : ''}><CalendarClock /><div><strong>{expiring}</strong><small>Istječe unutar 30 dana</small></div></article>
        <article><BadgeCheck /><div><strong>{waitingAck}</strong><small>Čeka moju potvrdu</small></div></article>
      </section>

      <section className="data-card document-toolbar-card">
        <header className="data-toolbar document-toolbar">
          <label className="compact-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Naziv, opis, zaposlenik, šifra ili e-mail…" /></label>
          <label className="compact-filter"><Filter /><select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}><option value="">Sve kategorije</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
          <select className="compact-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>{Object.entries(statusLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
          {canManage && <SearchableSelect value={ownerFilter} options={userOptions} onChange={setOwnerFilter} placeholder="Svi zaposlenici" searchPlaceholder="Ime, šifra ili e-mail…" allowClear />}
        </header>
      </section>

      {!isMobile && <section className="data-card document-desktop-list">
        <div className="business-table-wrap">
          <table className="business-table document-table">
            <thead><tr><th>Dokument</th><th>Zaposlenik</th><th>Rok važenja</th><th>Verzija i datoteka</th><th>Potvrda</th><th><span className="sr-only">Akcije</span></th></tr></thead>
            <tbody>{documents.map((document) => {
              const state = expiryState(document);
              return <tr key={document.id} tabIndex={0} onClick={() => void openDetail(document)} onKeyDown={(event) => { if (event.key === 'Enter') void openDetail(document); }}>
                <td><span className="document-table__identity"><span className="document-icon" style={{ color: document.categoryColor, background: `${document.categoryColor}15` }}><FileText /></span><span><strong>{document.title}</strong><small style={{ color: document.categoryColor }}>{document.categoryName}</small></span></span></td>
                <td><span className="table-primary">{document.ownerName ?? (document.visibility === 'COMPANY_ADMINS' ? 'Administratori firme' : 'Cijela firma')}</span></td>
                <td><span className={`document-expiry document-expiry--${state.toLowerCase()}`}>{expiryLabel(state)}</span><small className="table-secondary">{document.validUntil ? formatDate(document.validUntil) : 'Nema datuma isteka'}</small></td>
                <td><span className="table-primary">v{document.version.number} · {document.version.filename}</span><small className="table-secondary">{formatSize(document.version.sizeBytes)}</small></td>
                <td>{document.requiresAcknowledgement ? document.acknowledgedAt ? <span className="ack-badge"><BadgeCheck /> Pročitano</span> : <span className="document-ack-needed"><BadgeCheck /> Potrebna potvrda</span> : <span className="table-secondary">Nije potrebna</span>}</td>
                <td><span className="business-table__actions"><button className="icon-button" onClick={(event) => { event.stopPropagation(); void showPreview(document); }} title="Pregled"><Eye /></button><button className="icon-button" onClick={(event) => { event.stopPropagation(); void download(document); }} title={t('action.download')}><Download /></button>{canManage && <button className="icon-button" onClick={(event) => { event.stopPropagation(); openEdit(document); }} title="Uredi"><Pencil /></button>}<ChevronRight /></span></td>
              </tr>;
            })}</tbody>
          </table>
          {!loading && !documents.length && <div className="table-empty"><FileText />{t('documents.empty')}</div>}
        </div>
      </section>}

      <MobileVirtualList
        className="document-mobile-list"
        items={documents}
        estimateSize={255}
        getKey={(document) => document.id}
        empty={!loading ? <div className="table-empty"><FileText />{t('documents.empty')}</div> : null}
        renderItem={(document) => {
          const state = expiryState(document);
          return <SwipeReveal actionLabel="Pregled" ActionIcon={Eye} onAction={() => void showPreview(document)}>
            <article className={`document-card document-card--${state.toLowerCase()} mobile-native-card`} onClick={() => void openDetail(document)}>
              <header><span className="document-icon" style={{ color: document.categoryColor, background: `${document.categoryColor}15` }}><FileText /></span><span className="eyebrow" style={{ color: document.categoryColor }}>{document.categoryName}</span><span className={`document-expiry document-expiry--${state.toLowerCase()}`}>{expiryLabel(state)}</span></header>
              <div className="document-card__body"><h3>{document.title}</h3>{document.description && <p>{document.description}</p>}<div><span><UserRound /> {document.ownerName ?? (document.visibility === 'COMPANY_ADMINS' ? 'Administratori firme' : 'Cijela firma')}</span>{document.validUntil && <span><CalendarClock /> {formatDate(document.validUntil)}</span>}</div><small>v{document.version.number} · {document.version.filename} · {formatSize(document.version.sizeBytes)}</small></div>
              <footer>{document.requiresAcknowledgement && !document.acknowledgedAt && <span className="document-ack-needed"><BadgeCheck /> Potrebna potvrda</span>}{document.acknowledgedAt && <span className="ack-badge"><BadgeCheck /> Pročitano</span>}<span /><button className="icon-button" onClick={(event) => { event.stopPropagation(); void showPreview(document); }} title="Pregled"><Eye /></button><button className="icon-button" onClick={(event) => { event.stopPropagation(); void download(document); }} title={t('action.download')}><Download /></button><ChevronRight /></footer>
            </article>
          </SwipeReveal>;
        }}
      />

      {canCategories && categories.length > 0 && <section className="category-strip">{categories.map((category) => <button key={category.id} onClick={() => openCategory(category)}><i style={{ background: category.color }} /><span><strong>{category.name}</strong><small>{documentCountText(category.documentCount)}</small></span><Pencil /></button>)}</section>}

      {modal && <div className="modal-backdrop"><form className="operational-modal document-editor-modal" onSubmit={submit}><header><div><span className="eyebrow">{editing ? 'UREĐIVANJE DOKUMENTA' : 'NOVA DATOTEKA'}</span><h2>{editing ? 'Uredi dokument' : t('documents.new')}</h2></div><button type="button" className="icon-button" onClick={() => setModal(false)}><X /></button></header><div className="modal-scroll">
        {!editing && <label className={`file-drop ${form.fileDataUrl ? 'has-file' : ''}`}><FilePlus2 /><span><strong>{form.originalFilename || 'Odaberi dokument'}</strong><small>PDF, Word, Excel, TXT ili slika · najviše 8 MB</small></span><input required type="file" accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp" onChange={(event) => void chooseFile(event)} /></label>}
        <div className="form-grid"><label className="field field--full"><span>Naslov</span><input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Npr. Ugovor o radu 2026." /></label><label className="field"><span>Kategorija</span><select required value={form.categoryId} onChange={(event) => categoryChanged(event.target.value)}><option value="">Odaberi kategoriju</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label><div className="field"><span>Zaposlenik / vlasnik</span><SearchableSelect value={form.ownerUserId} options={userOptions} onChange={(value) => setForm({ ...form, ownerUserId: value, visibility: !value && !['OWNER', 'COMPANY_ADMINS'].includes(form.visibility) ? 'OWNER' : form.visibility })} placeholder="Cijela firma" searchPlaceholder="Ime, šifra ili e-mail…" allowClear /></div><label className="field field--full"><span>Tko smije vidjeti dokument</span><select value={form.visibility} onChange={(event) => setForm({ ...form, visibility: event.target.value })}><option value="OWNER">{form.ownerUserId ? 'Samo zaposlenik (i administrator)' : 'Svi zaposlenici'}</option>{form.ownerUserId && <option value="MENTOR">Zaposlenik, mentor i administrator</option>}{form.ownerUserId && <option value="TEAM_MANAGERS">Zaposlenik, voditelj tima i administrator</option>}<option value="COMPANY_ADMINS">{form.ownerUserId ? 'Zaposlenik i administratori firme' : 'Samo administratori firme'}</option></select><small>{visibilityLabels[form.visibility]}</small></label><label className="field field--full"><span>Opis</span><textarea rows={3} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label><label className="field"><span>Vrijedi od</span><CroatianDateInput value={form.validFrom} onValueChange={(value) => setForm({ ...form, validFrom: value })} ariaLabel="Vrijedi od" /></label><label className="field"><span>Vrijedi do</span><CroatianDateInput min={form.validFrom} value={form.validUntil} onValueChange={(value) => setForm({ ...form, validUntil: value })} ariaLabel="Vrijedi do" /></label><label className="checkbox-field field--full"><input type="checkbox" checked={form.requiresAcknowledgement} onChange={(event) => setForm({ ...form, requiresAcknowledgement: event.target.checked })} /><span><strong>Traži potvrdu čitanja</strong><small>Korisnik mora potvrditi svaku novu verziju dokumenta.</small></span></label></div>{modalError && <div className="form-error">{modalError}</div>}</div><footer><button type="button" className="button button--ghost" onClick={() => setModal(false)}>{t('action.cancel')}</button><button className="button button--primary" disabled={busy || (!editing && !form.fileDataUrl)}>{busy ? 'Sigurno spremam…' : t('action.save')}</button></footer></form></div>}

      {detail && <div className="modal-backdrop"><div className="operational-modal document-detail-modal"><header><div><span className="eyebrow" style={{ color: detail.categoryColor }}>{detail.categoryName} · VERZIJA {detail.version.number}</span><h2>{detail.title}</h2></div><button className="icon-button" onClick={() => setDetail(null)}><X /></button></header><div className="modal-scroll"><section className="document-detail-hero"><span className="document-icon" style={{ color: detail.categoryColor, background: `${detail.categoryColor}18` }}><FileText /></span><div><strong>{detail.ownerName ?? (detail.visibility === 'COMPANY_ADMINS' ? 'Administratori firme' : 'Cijela firma')}</strong><small>{visibilityLabels[detail.visibility]}</small></div><ShieldCheck /></section>{detail.description && <p className="document-detail-description">{detail.description}</p>}<div className="document-detail-grid"><article><CalendarClock /><span><small>Vrijedi od</small><strong>{detail.validFrom ? formatDate(detail.validFrom) : 'Nije određeno'}</strong></span></article><article><CalendarClock /><span><small>Vrijedi do</small><strong>{detail.validUntil ? formatDate(detail.validUntil) : 'Bez roka'}</strong></span></article><article><History /><span><small>Trenutačna verzija</small><strong>v{detail.version.number} · {formatSize(detail.version.sizeBytes)}</strong></span></article><article><FileCheck2 /><span><small>Datoteka</small><strong>{detail.version.filename}</strong></span></article></div>{versions.length > 1 && <details className="document-version-list"><summary>Povijest verzija ({versions.length})</summary>{versions.map((version) => <div key={version.id}><span><strong>Verzija {version.number} · {version.filename}</strong><small>{version.uploadedByName} · {formatDateTime(version.createdAt)} · {formatSize(version.sizeBytes)}</small></span><button className="icon-button" onClick={() => void downloadVersion(detail.id, version)} title="Preuzmi ovu verziju"><Download /></button></div>)}</details>}{detail.requiresAcknowledgement && <section className="document-ack-panel"><BadgeCheck /><div><strong>{detail.acknowledgedAt ? 'Čitanje je potvrđeno' : 'Dokument traži potvrdu čitanja'}</strong><small>{canManage ? `${detail.acknowledgementCount} evidentiranih potvrda trenutačne verzije` : 'Potvrda se evidentira uz datum, vrijeme i verziju.'}</small></div>{!detail.acknowledgedAt && <button className="button button--success button--small" disabled={busy} onClick={() => void acknowledge(detail)}>Potvrdi čitanje</button>}</section>}{canManage && acknowledgements.length > 0 && <details className="document-ack-list"><summary>Prikaži evidentirane potvrde ({acknowledgements.length})</summary>{acknowledgements.map((item) => <div key={item.userId}><span><strong>{item.userName}</strong><small>Verzija {item.versionNumber}</small></span><time>{formatDateTime(item.acknowledgedAt)}</time></div>)}</details>}{modalError && <div className="form-error">{modalError}</div>}</div><footer>{canManage && <button className="button button--soft-danger" onClick={() => setArchiveTarget(detail)}><Archive /> Arhiviraj</button>}<span />{canManage && <button className="button button--ghost" onClick={() => { setVersionFile({ originalFilename: '', fileDataUrl: '' }); setModalError(''); setVersionModal(true); }}><History /> Nova verzija</button>}{canManage && <button className="button button--ghost" onClick={() => { setDetail(null); openEdit(detail); }}><Pencil /> Uredi</button>}<button className="button button--ghost" onClick={() => void showPreview(detail)}><Eye /> Pregled</button><button className="button button--primary" onClick={() => void download(detail)}><Download /> Preuzmi</button></footer></div></div>}

      {versionModal && detail && <div className="modal-backdrop modal-backdrop--nested"><form className="mini-modal" onSubmit={uploadVersion}><header><div><span className="eyebrow">NOVA VERZIJA DOKUMENTA</span><h2>{detail.title}</h2></div><button type="button" className="icon-button" onClick={() => setVersionModal(false)}><X /></button></header><div className="info-banner">Nova datoteka postaje aktivna verzija {detail.version.number + 1}. Stara verzija ostaje u sigurnom tragu, a potvrde čitanja računaju se za novu verziju.</div><label className={`file-drop ${versionFile.fileDataUrl ? 'has-file' : ''}`}><History /><span><strong>{versionFile.originalFilename || 'Odaberi novu datoteku'}</strong><small>PDF, Word, Excel, TXT ili slika · najviše 8 MB</small></span><input required type="file" accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp" onChange={(event) => void chooseFile(event, true)} /></label>{modalError && <div className="form-error">{modalError}</div>}<footer><button type="button" className="button button--ghost" onClick={() => setVersionModal(false)}>Odustani</button><button className="button button--primary" disabled={busy || !versionFile.fileDataUrl}>{busy ? 'Spremam…' : `Objavi verziju ${detail.version.number + 1}`}</button></footer></form></div>}

      {archiveTarget && <div className="modal-backdrop modal-backdrop--nested"><div className="mini-modal"><header><div><span className="eyebrow">SIGURNO ARHIVIRANJE</span><h2>Arhiviraj dokument?</h2></div><button className="icon-button" onClick={() => setArchiveTarget(null)}><X /></button></header><div className="category-delete-copy"><strong>{archiveTarget.title}</strong><p>Dokument više neće biti vidljiv zaposlenicima. Datoteka i evidencija ostaju sačuvane radi sigurnosnog traga.</p></div>{modalError && <div className="form-error">{modalError}</div>}<footer><button className="button button--ghost" onClick={() => setArchiveTarget(null)}>Odustani</button><button className="button button--danger" disabled={busy} onClick={() => void archiveDocument()}><Archive /> Arhiviraj</button></footer></div></div>}

      {categoryModal && <div className="modal-backdrop"><form className="mini-modal" onSubmit={saveCategory}><header><div><span className="eyebrow">ŠIFARNIK</span><h2>{editingCategory ? 'Uredi kategoriju' : 'Nova kategorija'}</h2></div><button type="button" className="icon-button" onClick={() => setCategoryModal(false)}><X /></button></header><label className="field"><span>Šifra</span><input required value={categoryForm.code} onChange={(event) => setCategoryForm({ ...categoryForm, code: event.target.value.toUpperCase().replace(/\s/g, '_') })} /></label><label className="field"><span>Naziv</span><input required value={categoryForm.name} onChange={(event) => setCategoryForm({ ...categoryForm, name: event.target.value })} /></label><label className="field"><span>Opis</span><textarea value={categoryForm.description} onChange={(event) => setCategoryForm({ ...categoryForm, description: event.target.value })} /></label><label className="field"><span>Boja</span><input type="color" value={categoryForm.color} onChange={(event) => setCategoryForm({ ...categoryForm, color: event.target.value })} /></label>{editingCategory && <p className="muted category-document-count">U kategoriji: {documentCountText(editingCategory.documentCount)}</p>}{categoryError && <div className="form-error">{categoryError}</div>}<footer className="category-modal-footer">{editingCategory && <button type="button" className="button button--danger" onClick={askToDeleteCategory}><Trash2 /> Obriši kategoriju</button>}<button className="button button--primary" disabled={busy}>Spremi kategoriju</button></footer></form></div>}

      {deleteCategoryTarget && <div className="modal-backdrop"><form className="mini-modal" onSubmit={deleteCategory}><header><div><span className="eyebrow">SIGURNO BRISANJE</span><h2>Obriši kategoriju</h2></div><button type="button" className="icon-button" onClick={() => setDeleteCategoryTarget(null)}><X /></button></header><div className="category-delete-copy"><strong>{deleteCategoryTarget.name}</strong><p>{deleteCategoryTarget.documentCount > 0 ? `Kategorija sadrži ${documentCountText(deleteCategoryTarget.documentCount)}. Prije brisanja odaberite kamo ih treba premjestiti.` : 'Kategorija nema dokumenata i može se sigurno obrisati.'}</p></div>{deleteCategoryTarget.documentCount > 0 && <label className="field"><span>Premjesti dokumente u</span><select required value={replacementCategoryId} onChange={(event) => setReplacementCategoryId(event.target.value)}><option value="">Odaberi drugu kategoriju</option>{categories.filter((category) => category.id !== deleteCategoryTarget.id).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>}{deleteCategoryTarget.documentCount > 0 && categories.length < 2 && <div className="form-error">Prvo izradite drugu kategoriju u koju se dokumenti mogu premjestiti.</div>}{categoryError && <div className="form-error">{categoryError}</div>}<footer><button type="button" className="button button--ghost" onClick={() => setDeleteCategoryTarget(null)}>Odustani</button><button className="button button--danger" disabled={busy || (deleteCategoryTarget.documentCount > 0 && !replacementCategoryId)}><Trash2 /> {deleteCategoryTarget.documentCount > 0 ? 'Premjesti i obriši' : 'Obriši kategoriju'}</button></footer></form></div>}

      {preview && <div className="modal-backdrop document-preview-backdrop"><section className="document-preview"><header><div><span className="eyebrow">SIGURNI PREGLED</span><h2>{preview.title}</h2></div><button className="icon-button" onClick={closePreview}><X /></button></header>{preview.mimeType.startsWith('image/') ? <img src={preview.url} alt={preview.title} /> : <iframe src={preview.url} title={preview.title} />}</section></div>}
    </div>
  );
}
